import Image from '@/components/Image';
import { LandingPagePathsQuery, SemLandingPageQuery, SemLandingPageQueryVariables } from '@/graphql';
import LandingPagePathsQueryDocument from '@/graphql/queries/LandingPagePaths.gql';
import SEMLandingPageQueryDocument from '@/graphql/queries/SEMLandingPage.gql';
import ModularPageLayout from '@/layouts/ModularPageLayout';
import ComparisonModulesRenderer from '@/components/_rebrand/craft/ComparisonModulesRenderer';
import { getPageProps } from '@/lib/craft-page';
import CraftClient from '@/lib/graphql';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { getSingle, notEmpty } from '@/lib/typescript-helpers';
import { ModularPageProps, ParseModuleBuilderPropsInput, parseModuleBuilderProps } from '@/modules/ModuleBuilder';
import LandingPageTemplate from '@/templates/LandingPageTemplate';
import { FC, ReactNode, useMemo } from 'react';

const LandingPage: PageTemplate<SemLandingPageQuery | ModularPageProps> = (props) => {
  const isModularPageProps = 'modularPageProps' in props;

  const moduleData = useMemo(() => {
    if (!isModularPageProps) return null;
    if (!props.modularPageProps.entry?.moduleBuilder) return null;

    const filteredModuleBuilder = props.modularPageProps.entry.moduleBuilder
      .filter(notEmpty)
      .map((_module) => {
        if (!('module' in _module) || !_module.module[0]) return null;
        return _module;
      })
      .filter(notEmpty);

    return {
      ...props.modularPageProps,
      entry: {
        ...props.modularPageProps.entry,
        moduleBuilder: filteredModuleBuilder,
      },
    };
  }, [isModularPageProps, isModularPageProps ? props.modularPageProps : null]);

  if (isModularPageProps) {
    if (!moduleData) return null;

    return <ModularPageLayout props={moduleData} />;
  }

  const entry = 'entry' in props ? props.entry : null;
  const isRebrandModularEntry = entry?.__typename === 'landingPages_rebrandModular_Entry';

  if (entry && isRebrandModularEntry) {
    return <ComparisonModulesRenderer modules={entry.comparisonModules} />;
  }

  return null;
};

LandingPage.template = (({ entry, children }) => {
  let additionalLockupLogo: ReactNode = null;

  const lockupLogo = useMemo(() => {
    if (entry?.__typename !== 'landingPages_sem_Entry') return null;

    const logo = getSingle(getSingle(entry.logo)?.lockupLogo);
    const customerLogo = getSingle(getSingle(entry.logo)?.customerLogo);

    return logo ?? customerLogo;
  }, [entry]);

  if (entry?.__typename === 'landingPages_sem_Entry' && lockupLogo) {
    additionalLockupLogo = (
      <Image src={lockupLogo?.url!} width={lockupLogo?.width!} height={lockupLogo?.height!} alt="" />
    );
  }

  return <LandingPageTemplate additionalLockupLogo={additionalLockupLogo}>{children}</LandingPageTemplate>;
}) satisfies FC<PageTemplateProps<SemLandingPageQuery>>;

export default LandingPage;

export const getStaticPaths = getStaticPagePaths('lp', async (context) => {

  const client = new CraftClient();

  const data = await client.request<LandingPagePathsQuery>(LandingPagePathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<{}, { slug: string }>(
  (params) => `lp/${params?.slug}`,
  async ({ client, context: { params } }) => {

    const pageProps = await getPageProps<SemLandingPageQuery, SemLandingPageQueryVariables>({
      pathPrefix: '/lp',
      queryDocument: SEMLandingPageQueryDocument,
      client,
      variables: {
        slug: params?.slug,
      },
    });

    if (!pageProps?.entry) {
      return { notFound: true };
    }

    if (pageProps.entry.__typename === 'landingPages_sem_Entry') {
      return {
        props: parseModuleBuilderProps(pageProps as ParseModuleBuilderPropsInput)
      }
    }

    return {
      props: {
        ...pageProps,
      },
    };
  },
);
